/**
 * Main cost trends visualization - logarithmic timeline chart
 */

import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChartDataPoint, ModelSummary } from './types'

interface CostTrendsChartProps {
  summaries: ModelSummary[]
  priceType: 'input' | 'output'
  highlightedModel?: string | null
  onModelHover?: (modelId: string | null) => void
}


export const CostTrendsChart: React.FC<CostTrendsChartProps> = ({
  summaries,
  priceType,
  highlightedModel,
  onModelHover,
}) => {
  const { t } = useTranslation('aiCostTrends')
  const [hoveredPoint, setHoveredPoint] = useState<ChartDataPoint | null>(null)

  // Chart dimensions
  const chartWidth = 800
  const chartHeight = 400
  const padding = { top: 40, right: 120, bottom: 60, left: 70 }

  // Compute scales
  const { xScale, yScale, xTicks, yTicks } = useMemo(() => {
    // Collect all data points
    const allPoints = summaries.flatMap((s) => s.dataPoints)
    if (allPoints.length === 0) {
      return {
        xScale: () => 0,
        yScale: () => 0,
        xTicks: [],
        yTicks: [],
      }
    }

    // X scale (time)
    const minTime = Math.min(...allPoints.map((p) => p.timestamp))
    const maxTime = Math.max(...allPoints.map((p) => p.timestamp))
    const timeRange = maxTime - minTime || 1

    const xScale = (timestamp: number) => {
      return (
        padding.left +
        ((timestamp - minTime) / timeRange) *
          (chartWidth - padding.left - padding.right)
      )
    }

    // Y scale (logarithmic for price)
    const prices = allPoints.map((p) =>
      priceType === 'input' ? p.inputPer1M : p.outputPer1M
    )
    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)

    // Use log scale with nice bounds
    const logMin = Math.floor(Math.log10(Math.max(minPrice, 0.01)))
    const logMax = Math.ceil(Math.log10(maxPrice))

    const yScale = (price: number) => {
      const logPrice = Math.log10(Math.max(price, 0.01))
      const normalized = (logPrice - logMin) / (logMax - logMin || 1)
      return (
        chartHeight -
        padding.bottom -
        normalized * (chartHeight - padding.top - padding.bottom)
      )
    }

    // Generate x ticks (years)
    const startYear = new Date(minTime).getFullYear()
    const endYear = new Date(maxTime).getFullYear()
    const xTicks: { value: number; label: string }[] = []
    for (let year = startYear; year <= endYear; year++) {
      const timestamp = new Date(year, 0, 1).getTime()
      if (timestamp >= minTime && timestamp <= maxTime) {
        xTicks.push({ value: timestamp, label: year.toString() })
      }
    }

    // Generate y ticks (powers of 10)
    const yTicks: { value: number; label: string }[] = []
    for (let exp = logMin; exp <= logMax; exp++) {
      const value = Math.pow(10, exp)
      yTicks.push({
        value,
        label: value >= 1 ? `$${value}` : `$${value.toFixed(2)}`,
      })
    }

    return { xScale, yScale, xTicks, yTicks }
  }, [summaries, priceType, chartWidth, chartHeight])

  if (summaries.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-secondary">
        {t('chart.noData')}
      </div>
    )
  }

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Grid lines */}
        {yTicks.map((tick) => (
          <g key={tick.value}>
            <line
              x1={padding.left}
              y1={yScale(tick.value)}
              x2={chartWidth - padding.right}
              y2={yScale(tick.value)}
              stroke="currentColor"
              strokeOpacity={0.1}
              strokeDasharray="4,4"
            />
            <text
              x={padding.left - 10}
              y={yScale(tick.value) + 4}
              textAnchor="end"
              className="fill-current text-secondary"
              fontSize="11"
            >
              {tick.label}
            </text>
          </g>
        ))}

        {/* X axis ticks */}
        {xTicks.map((tick) => (
          <g key={tick.value}>
            <line
              x1={xScale(tick.value)}
              y1={chartHeight - padding.bottom}
              x2={xScale(tick.value)}
              y2={chartHeight - padding.bottom + 5}
              stroke="currentColor"
              strokeOpacity={0.3}
            />
            <text
              x={xScale(tick.value)}
              y={chartHeight - padding.bottom + 20}
              textAnchor="middle"
              className="fill-current text-secondary"
              fontSize="12"
            >
              {tick.label}
            </text>
          </g>
        ))}

        {/* Y axis label */}
        <text
          x={20}
          y={chartHeight / 2}
          textAnchor="middle"
          className="fill-current text-secondary"
          fontSize="11"
          transform={`rotate(-90, 20, ${chartHeight / 2})`}
        >
          {t('chart.yAxisLabel')}
        </text>

        {/* X axis label */}
        <text
          x={chartWidth / 2}
          y={chartHeight - 10}
          textAnchor="middle"
          className="fill-current text-secondary"
          fontSize="11"
        >
          {t('chart.xAxisLabel')}
        </text>

        {/* Lines and points for each model */}
        {summaries.map((summary) => {
          const points = summary.dataPoints
          const isHighlighted =
            !highlightedModel || highlightedModel === summary.modelId
          const opacity = highlightedModel
            ? isHighlighted
              ? 1
              : 0.15
            : 0.8

          // Create line path
          const linePath = points
            .map((p, i) => {
              const x = xScale(p.timestamp)
              const y = yScale(
                priceType === 'input' ? p.inputPer1M : p.outputPer1M
              )
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
            })
            .join(' ')

          return (
            <g
              key={summary.modelId}
              opacity={opacity}
              onMouseEnter={() => onModelHover?.(summary.modelId)}
              onMouseLeave={() => onModelHover?.(null)}
              style={{ cursor: 'pointer' }}
            >
              {/* Line */}
              {points.length > 1 && (
                <path
                  d={linePath}
                  fill="none"
                  stroke={summary.providerColor}
                  strokeWidth={isHighlighted ? 2.5 : 1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* Points with labels */}
              {points.map((point, i) => {
                const x = xScale(point.timestamp)
                const y = yScale(
                  priceType === 'input' ? point.inputPer1M : point.outputPer1M
                )
                // Alternate label position: odd indices above, even below
                const labelOffset = i % 2 === 0 ? -12 : 18

                return (
                  <g key={`${point.modelId}-${i}`}>
                    <circle
                      cx={x}
                      cy={y}
                      r={isHighlighted ? 5 : 4}
                      fill="var(--color-bg-primary)"
                      stroke={summary.providerColor}
                      strokeWidth={2}
                      onMouseEnter={() => setHoveredPoint(point)}
                      onMouseLeave={() => setHoveredPoint(null)}
                    />
                    {/* Version label on each point */}
                    {isHighlighted && point.label && (
                      <text
                        x={x}
                        y={y + labelOffset}
                        textAnchor="middle"
                        className="fill-current pointer-events-none"
                        fontSize="9"
                        fontWeight="500"
                        style={{ fill: summary.providerColor }}
                      >
                        {point.label}
                      </text>
                    )}
                  </g>
                )
              })}

            </g>
          )
        })}
      </svg>

      {/* Tooltip */}
      {hoveredPoint && (
        <div
          className="absolute bg-primary border border-secondary rounded-lg p-3 shadow-lg z-10 pointer-events-none"
          style={{
            left: '50%',
            top: '10px',
            transform: 'translateX(-50%)',
            minWidth: '200px',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: hoveredPoint.providerColor }}
            />
            <span className="font-semibold text-primary">
              {hoveredPoint.label || hoveredPoint.modelName}
            </span>
          </div>
          <div className="text-sm space-y-1 text-secondary">
            <div className="flex justify-between">
              <span>{t('tooltip.date')}:</span>
              <span className="font-mono">
                {new Date(hoveredPoint.date).toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t('tooltip.input')}:</span>
              <span className="font-mono">
                ${hoveredPoint.inputPer1M.toFixed(2)}/1M
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t('tooltip.output')}:</span>
              <span className="font-mono">
                ${hoveredPoint.outputPer1M.toFixed(2)}/1M
              </span>
            </div>
            {hoveredPoint.notes && (
              <div className="text-xs text-accent mt-1">
                {hoveredPoint.notes}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CostTrendsChart
